import { Fragment as _Fragment, jsxs as _jsxs, jsx as _jsx } from "react/jsx-runtime";
import { DoorWindow, Required, } from "@countdown/theme";
import { Fragment, useEffect, useLayoutEffect, useRef, useState, } from "react";
import { createPortal } from "react-dom";
import { ALT_TEXT_MISSING, FIELD_NAMES, MAX_OPTIONS, MAX_QUESTIONS, MAX_SLIDES, MIN_OPTIONS, OPTION_KEYS, adopt, documentOf, emptyQuestion, emptySlide, fieldId, formOf, isEmptyQuestion, isVideo, isVideoFile, missingTarget, pictureAt, problemsOf, uploadPicture, uploadProblem, } from "./door.js";
import { CropDialog, cutOut } from "./CropDialog.js";
import { EditableText } from "./EditableText.js";
import { ELEMENT_LABELS, MAX_LABEL, droppedElement, fitLayout, fullLayout, retrySlideIn, withStep, } from "./layout.js";
import { LayoutSettings } from "./LayoutSettings.js";
import { EMPTY_SLOT, pendingFiles, slotKey } from "./pending.js";
import { Help, Icon } from "./controls.js";
import { PictureSlot } from "./PictureSlot.js";
import { EMPTY_PICTURE, plainBlocks, standIn, withCounts, wrongAnswerWords } from "./preview.js";
import { InRow, RowHead, rowsOf } from "./rows.js";
import { ADD_CLASS, BUTTON_CLASS, CHOOSABLE_CLASS, CHOSEN_CLASS, CONTROLS_CLASS, FIELD_CLASS, GRIP_CLASS, GRIP_HIDDEN_CLASS, GRIPS_CLASS, ICON_CLASS, LABEL_CLASS, MOVABLE_CLASS, NOTE_CLASS, PRIMARY_CLASS, QUIET_CLASS, ROW_CLASS, TAB_CLASS, TAB_CURRENT_CLASS, TAB_GROUP_CLASS, TAB_IN_GROUP_CLASS, TAB_REMOVE_CLASS, TAB_TOOL_CLASS, TAB_TOOLS_CLASS, } from "./styles.js";
/**
 * The formatting the one parser on the server accepts (`services/richtext.py`). Behind the »?«
 * beside the preview, where the texts are typed.
 */
function formatHint(links) {
    const link = links ? ", ein Link als [Text](https://…), höchstens 5 Links" : "";
    return `Formatierung: **fett**, *kursiv*, eine Liste mit »- « am Zeilenanfang${link}. Eine Leerzeile beginnt einen neuen Absatz. Überschriften und HTML sind nicht erlaubt.`;
}
/** No bar: the configurator keeps its own "Speichern". */
const NO_BAR = () => false;
/** A paragraph with a space: the window renders the text element, the editor replaces it. */
const EDITABLE_BLOCK = [{ kind: "paragraph", lines: [[{ text: " " }]] }];
/** Two parts of the settings, or `null` where neither shows. */
function both(first, second) {
    return first === null && second === null ? null : (_jsxs(_Fragment, { children: [first, second] }));
}
/** The step a rail entry names, with a removed slide falling back to the first one. */
function resolved(step, form) {
    if (step.kind !== "slide" || form.slides.some((slide) => slide.key === step.key)) {
        return step;
    }
    const first = form.slides[0];
    return first === undefined ? { kind: "quiz" } : { kind: "slide", key: first.key };
}
function sameStep(left, right) {
    return left.kind === right.kind && (left.kind !== "slide" || right.kind !== "slide" || left.key === right.key);
}
/** The step that holds a slot's settings. */
function stepOfTarget(target) {
    if (target.kind === "prize") {
        return { kind: "right" };
    }
    return target.kind === "question" ? { kind: "quiz" } : { kind: "slide", key: target.key };
}
/** What a slot takes, by its kind. */
function acceptsOf(target, videos) {
    if (target.kind === "captions") {
        return "captions";
    }
    return target.kind === "slide" && videos ? "slide" : "picture";
}
/** The id prefix of a slot's controls, as the slot names its inputs. */
function slotPrefix(position, target) {
    switch (target.kind) {
        case "prize":
            return fieldId(position, "gewinnbild");
        case "poster":
            return fieldId(position, "vorschaubild", target.key);
        case "captions":
            return fieldId(position, "untertitel", target.key);
        default:
            return fieldId(position, "bild", target.key);
    }
}
/**
 * The configurator of a door (D37): the door as one window, configured where it shows.
 *
 * One column (D88, 23.10.2026), no settings column beside it: the tabs of the window's steps
 * (Folie 1 to n, "+", Quiz, Gewinn, Falsch; in the portal »Falsche Antwort«; the fallback
 * content has its one step), the shown slide's tab with its buttons to move and remove it, and
 * below them the window itself (`DoorWindow` of the theme, the component of the participant
 * page) as wide as the participant sees it, live from the unsaved draft: its texts and button
 * labels are typed in place, a click on an element chooses it, and its grip moves it in the
 * step's order (22.10.2026). An empty element shows its place or its name, so it can still be chosen. The
 * settings of the chosen element (a picture's file, place and format, a button's kind and
 * address) stand in the window right under it, the number of prizes above the window. The
 * questions of the quiz are typed where the participant reads them, with their answers.
 *
 * **Not a second set of rules.** Every check lives on the server: the formatting, what a
 * submission still lacks, which states a sponsor may change. The form sends the whole door and
 * takes the server's answer as its new state, because only the answer knows the id of a new
 * question and which empty rows were dropped.
 *
 * **Pictures go to a slot of the stored door**, counted the way the server counts. An upload
 * saves the form first when it has changes, and then names the slot (`uploadPicture`). A file
 * chosen stays chosen across the steps, and the next save uploads it first (21.10.2026): no
 * upload button, no question. It can be cut before, and goes up with its frame (D88).
 *
 * **The page says where the door is written** (`writes`) and owns the layout (`layout`,
 * `onLayoutChange`): a booking's own layout, the action's standard or none at all. The
 * presentation (page or dialog) is the publisher's and only read here; it limits the formats.
 *
 * **On a page with a save bar the plain save is the bar's** (`joinSaveBar`): the configurator
 * joins as »Beitrag« and drops its own "Speichern"; "Speichern und einreichen" stays.
 */
export function DoorConfigurator(props) {
    const { door, writes, presentation, prizeQuantity = true, videos = true, maxSlides = MAX_SLIDES, slideStructure = true, quizEditable = true, prizeEditable = true, linkButtons = true, acceptUnscanned = false, sponsored = true, guided = false, fallback = false, missing = [], } = props;
    const position = door.field_position;
    const editable = writes !== undefined;
    const quizOpen = editable && quizEditable;
    const prizeOpen = editable && prizeEditable;
    const [form, setForm] = useState(() => formOf(door));
    // The form as the server last answered it, for "Ungespeicherte Änderungen".
    const [stored, setStored] = useState(form);
    // The copy of the door the form last took in, and whether a later copy brought a stand saved
    // elsewhere while the form held edits of its own.
    const [seen, setSeen] = useState(door);
    const [overtaken, setOvertaken] = useState(false);
    const [busy, setBusy] = useState(false);
    // Counted rather than flagged: every refused click renders, and the effect moves the focus.
    const [refusedChecks, setRefusedChecks] = useState(0);
    const [said, setSaid] = useState(null);
    // Pictures uploaded in this session by asset id, as the browser still holds them.
    const [pictures, setPictures] = useState({});
    const [chosenStep, setChosenStep] = useState(() => resolved({ kind: "slide", key: "" }, form));
    const [chosenElement, setChosenElement] = useState(null);
    // The element whose grip the mouse drags, and the one it is over.
    const dragged = useRef(null);
    const [over, setOver] = useState(null);
    const [drafts, setDrafts] = useState({});
    const [messages, setMessages] = useState({});
    const [unscanned, setUnscanned] = useState(() => new Set());
    // The picture in the cut dialog, with the slot it shows in: a stored one (D44) or a chosen
    // file before its upload (D88).
    const [cutting, setCutting] = useState(null);
    // The cut-out of a chosen file as the browser drew it, by slot, for the preview (D88).
    const [cuts, setCuts] = useState({});
    const addresses = useRef([]);
    const fileAddresses = useRef(new Map());
    const focusNext = useRef(null);
    // An element inside the preview: ids there resolve in the frame's own document.
    const inPreview = useRef(null);
    // The configurator itself: the crop dialog goes into a modal dialog around it, where there is
    // one (the backoffice's door dialog), since outside it the modal leaves nothing clickable.
    const root = useRef(null);
    // The form and its stored copy as the last write left them. A save that first uploads
    // several files runs on, between renders, with what each upload answered.
    const latest = useRef(form);
    const latestStored = useRef(stored);
    useLayoutEffect(() => {
        latest.current = form;
        latestStored.current = stored;
    });
    useEffect(() => {
        const files = fileAddresses.current;
        const uploaded = addresses.current;
        return () => {
            for (const address of [...uploaded, ...files.values()]) {
                URL.revokeObjectURL(address);
            }
        };
    }, []);
    // After every render: the focus a row operation or a finished write asked for, in this page
    // or in the preview's frame. A write disables the form, which takes the focus away from the
    // button that started it. The problem list goes to the middle, clear of a page's save bar.
    useEffect(() => {
        const target = focusNext.current;
        if (target !== null && !busy) {
            focusNext.current = null;
            const element = byId(target);
            element?.focus();
            if (target === id("probleme")) {
                element?.scrollIntoView({ block: "center" });
            }
        }
    });
    const id = (part, row) => fieldId(position, part, row);
    const focus = (target) => {
        focusNext.current = target;
    };
    /** An element by its id, in this page or in the document of the preview's frame. */
    const byId = (target) => document.getElementById(target) ?? inPreview.current?.ownerDocument.getElementById(target) ?? null;
    /** The focused element; with the focus in the preview's frame, the page sees only the frame. */
    const focused = () => {
        const active = document.activeElement;
        const inFrame = inPreview.current?.ownerDocument;
        return active instanceof HTMLIFrameElement && inFrame !== undefined && active.contentDocument === inFrame
            ? inFrame.activeElement
            : active;
    };
    const sent = (value) => JSON.stringify(documentOf(value, position));
    const changed = editable && sent(form) !== sent(stored);
    const order = [...form.slides, ...form.questions];
    const picked = pendingFiles(drafts, order);
    const dirty = changed || picked.length > 0;
    // From a refused click until one passes the list follows the form, so a corrected field
    // drops out of it, and a field that is still being filled in is not called wrong later.
    const problems = refusedChecks > 0 ? problemsOf(form, position) : [];
    const invalid = new Set(problems.map((problem) => problem.field));
    /** Marks an input with a problem and points it at the message in the list. */
    const flagged = (field) => ({
        "aria-invalid": invalid.has(field),
        "aria-describedby": invalid.has(field) ? `${field}-fehler` : undefined,
    });
    const step = resolved(chosenStep, form);
    const shape = fitLayout(fullLayout(props.layout), presentation);
    const layoutEditable = props.onLayoutChange !== undefined;
    const layoutShown = layoutEditable || (props.hideLockedLayout !== true && !guided);
    // Guided and nothing to write: the steps and the preview, no settings to look at.
    const viewOnly = guided && !editable && !layoutEditable;
    const slideLimit = fallback ? 1 : Math.min(MAX_SLIDES, maxSlides);
    const canAddSlide = editable && slideStructure && form.slides.length < slideLimit;
    /** A slide's name in the rail and in the labels; the fallback's one step has its own. */
    const slideName = (index) => (fallback ? "Ersatzinhalt" : `Folie ${String(index + 1)}`);
    /** The id of a step's button in the rail. */
    const railId = (railStep) => id("schritt", railStep.kind === "slide" ? railStep.key : railStep.kind);
    const slides = rowsOf(form.slides, (change) => {
        setForm((current) => ({ ...current, slides: change(current.slides) }));
    }, focus, id, { first: "titel", add: "folie-neu" });
    const questions = rowsOf(form.questions, (change) => {
        setForm((current) => ({ ...current, questions: change(current.questions) }));
    }, focus, id, { first: "frage", add: "frage-neu" });
    /** The form as the server answered it is the new state, and the new "saved". */
    function settle(next) {
        latest.current = next;
        latestStored.current = next;
        setForm(next);
        setStored(next);
        setOvertaken(false);
    }
    // Every write on the page reloads the door and hands in a new copy. Kept on the copy it
    // opened with, "Speichern" would write that over what was saved since, and an upload would
    // count its slot on rows the door no longer has. A form without edits takes the new copy;
    // edits stay, and the form says that "Speichern" replaces the newer stand. Adjusted during
    // the render rather than in an effect, so no render shows the old copy.
    if (door !== seen) {
        setSeen(door);
        if (!changed) {
            settle(adopt(form, door));
        }
        else {
            const current = adopt(stored, door);
            if (sent(current) !== sent(stored)) {
                setStored(current);
                setOvertaken(true);
            }
        }
    }
    const warned = overtaken && changed ? id("ueberholt") : undefined;
    const submitNoted = writes?.submit !== undefined && props.submitNote !== undefined
        ? id("einreichen-hinweis")
        : undefined;
    const submitDescribed = [submitNoted, warned].filter(Boolean).join(" ") || undefined;
    /** Chooses a step, and in it an element of the window. */
    function choose(next, element = null) {
        setChosenStep(next);
        setChosenElement(element);
    }
    /**
     * A link of the problem list or of »Noch offen«: the step of an input, the element whose
     * settings or text hold it, and the focus in it.
     */
    function jump(next, field, element = null) {
        choose(next, element);
        focus(field);
        // Already on the screen: no render follows that would move the focus.
        byId(field)?.focus();
    }
    /** Whether the input of a missing part is offered here at all; a right may leave it out. */
    function offered(key) {
        if (!editable) {
            return false;
        }
        switch (key) {
            case "prize_description":
                return prizeOpen;
            case "prize_quantity":
                return prizeOpen && prizeQuantity;
            case "video_poster":
            case "video_text":
            case "video_captions":
                return videos;
            case "slide_button_url":
                return linkButtons;
            case "sponsor_question":
            case "answer_options":
            case "correct_option_key":
                return quizOpen;
            default:
                return true;
        }
    }
    /**
     * Whether the form may be sent. Otherwise the list of problems above the buttons takes the
     * focus, also on the second click, when an unchanged alert would not be announced again.
     */
    function checked() {
        if (problemsOf(latest.current, position).length === 0) {
            setRefusedChecks(0);
            return true;
        }
        focus(id("probleme"));
        setRefusedChecks((count) => count + 1);
        props.onInvalid?.();
        return false;
    }
    /** One write with the form locked; its answer goes to the page, which announces it. */
    async function locked(write) {
        // An element of the frame is no `HTMLElement` of this window: read its id as it is.
        const active = focused();
        if (focusNext.current === null) {
            focusNext.current = active !== null && active.id !== "" ? active.id : null;
        }
        setBusy(true);
        let outcome;
        try {
            outcome = await write();
        }
        catch (error) {
            console.error("[DoorConfigurator] write failed", error);
            outcome = { ok: false, message: "Die Daten konnten nicht gespeichert werden." };
        }
        finally {
            setBusy(false);
        }
        props.onSaved?.(outcome);
        return outcome;
    }
    function write(submit) {
        return locked(async () => {
            if (writes === undefined) {
                return { ok: false, message: "Dieser Inhalt lässt sich hier nicht ändern." };
            }
            const result = await writes.save(documentOf(latest.current, position));
            if (!result.ok) {
                return result;
            }
            settle(adopt(latest.current, result.booking));
            if (!submit || writes.submit === undefined) {
                return { ok: true, message: "Gespeichert." };
            }
            const submitted = await writes.submit();
            return submitted.ok ? { ok: true, message: "Gespeichert und eingereicht." } : submitted;
        });
    }
    /**
     * The whole save: the checks, then the chosen files in the order of the door, then the door.
     * `invalid` when the problems or a missing description stopped it, as the save bar expects.
     */
    async function save(submit) {
        setSaid(null);
        if (!checked()) {
            return "invalid";
        }
        for (const file of pendingFiles(drafts, [...latest.current.slides, ...latest.current.questions])) {
            const uploaded = await uploadChosen(file.target);
            if (uploaded !== "ok") {
                return uploaded;
            }
        }
        const outcome = await write(submit);
        setSaid(outcome);
        return outcome.ok ? "ok" : "failed";
    }
    const inBar = (props.joinSaveBar ?? NO_BAR)(editable
        ? {
            label: props.saveLabel ?? "Beitrag",
            dirty,
            hint: props.saveHint,
            save: () => save(false),
        }
        : null);
    /**
     * An upload into one slot; it answers in its slot, the line below the buttons belongs to
     * the saves.
     */
    async function upload(target, file, altText, unscannedReason, crop = null) {
        const key = slotKey(target);
        const prefix = slotPrefix(position, target);
        // Started in the slot: its buttons go with the draft, the focus stays in the slot.
        const fromSlot = byId(`${prefix}-datei`)?.closest("fieldset")?.contains(focused()) === true;
        setSaid(null);
        const isDirty = sent(latest.current) !== sent(latestStored.current);
        if (writes === undefined) {
            return { ok: false, message: "Dieser Inhalt lässt sich hier nicht ändern." };
        }
        if (isDirty && !checked()) {
            const refusal = {
                ok: false,
                message: "Vor dem Hochladen wird gespeichert. Erst die Angaben über den Knöpfen korrigieren.",
            };
            setMessages((current) => ({ ...current, [key]: { text: refusal.message, alert: true } }));
            return refusal;
        }
        const what = target.kind === "poster"
            ? "Vorschaubild"
            : target.kind === "captions"
                ? "Untertitel"
                : isVideoFile(file)
                    ? "Video"
                    : "Bild";
        let partial = false;
        const outcome = await locked(async () => {
            const { outcome: answered, form: next, partial: moveFailed } = await uploadPicture(latest.current, position, target, isDirty, {
                save: writes.save,
                upload: (slot) => writes.upload({ slot, file, altText, unscannedReason, ...(crop === null ? {} : { crop }) }),
            }, what);
            partial = moveFailed === true;
            if (next !== undefined) {
                settle(next);
            }
            const placed = answered.ok && next !== undefined ? pictureAt(next, target) : null;
            // Shown from the browser's copy: a picture only. A video plays on the participant page.
            // A cut file shows its cut-out where the browser drew one, else what the host serves.
            const drawn = cuts[key];
            if (placed !== null && file.type.startsWith("image/")) {
                if (crop === null) {
                    const address = URL.createObjectURL(file);
                    addresses.current.push(address);
                    setPictures((current) => ({ ...current, [placed.asset_id]: address }));
                }
                else if (drawn?.file === file) {
                    setPictures((current) => ({ ...current, [placed.asset_id]: drawn.address }));
                }
            }
            return answered;
        });
        if (outcome.ok) {
            setDrafts((current) => {
                const rest = { ...current };
                delete rest[key];
                return rest;
            });
            if (fromSlot) {
                focus(`${prefix}-datei`);
            }
        }
        setUnscanned((current) => {
            const next = new Set(current);
            if (!outcome.ok && "unscanned" in outcome && outcome.unscanned === true) {
                next.add(key);
            }
            else {
                next.delete(key);
            }
            return next;
        });
        // Every answer stands where the upload started, also "die Folie steht aber am Ende"
        // after a picture that went up but did not reach its place. A failure, that one too, is
        // an alert, a success a polite status (»Bild hochgeladen.«).
        setMessages((current) => ({
            ...current,
            [key]: {
                text: outcome.message ?? "Erledigt.",
                alert: !outcome.ok || partial,
                announced: props.onSaved !== undefined,
            },
        }));
        return outcome;
    }
    /**
     * Uploads the file chosen in a slot, after the checks the server would make first. A
     * refusal here chooses the slot's step and puts the focus where the problem is.
     */
    async function uploadChosen(target, unscannedReason) {
        const key = slotKey(target);
        const chosen = drafts[key] ?? { ...EMPTY_SLOT, target };
        const accepts = acceptsOf(target, videos);
        const problem = uploadProblem(chosen.file, chosen.altText, accepts);
        if (chosen.file === null || problem !== null) {
            // A slot stands under the window's picture; from the second question on in its question.
            const inList = target.kind === "question" && target.key !== latest.current.questions[0]?.key;
            choose(stepOfTarget(target), inList ? null : "image");
            const prefix = slotPrefix(position, target);
            focus(chosen.file !== null && problem === ALT_TEXT_MISSING ? `${prefix}-alt` : `${prefix}-datei`);
            setMessages((current) => ({ ...current, [key]: { text: problem ?? "", alert: true } }));
            props.onInvalid?.();
            return "invalid";
        }
        const sentText = chosen.altText.trim();
        const outcome = await upload(target, chosen.file, accepts === "captions" || isVideoFile(chosen.file) ? "" : sentText, unscannedReason, chosen.crop);
        return outcome.ok ? "ok" : "failed";
    }
    /**
     * A new cut of the picture a slot shows (D44), saved like an upload: the form first when it
     * has changes, then the slot as the server counts it. The answer stands in the slot.
     */
    async function cut(target, picture, frame) {
        const key = slotKey(target);
        const recut = writes?.recut;
        if (writes === undefined || recut === undefined) {
            return;
        }
        const isDirty = sent(latest.current) !== sent(latestStored.current);
        if (isDirty && !checked()) {
            setCutting(null);
            return;
        }
        const outcome = await locked(async () => {
            const { outcome: answered, form: next } = await uploadPicture(latest.current, position, target, isDirty, {
                save: writes.save,
                upload: (slot) => recut({ slot, assetId: picture.asset_id, crop: frame }),
            });
            if (next !== undefined) {
                settle(next);
            }
            return answered;
        });
        if (outcome.ok) {
            setCutting(null);
        }
        setMessages((current) => ({
            ...current,
            [key]: {
                text: outcome.ok ? "Bild zugeschnitten." : outcome.message,
                alert: !outcome.ok,
                announced: props.onSaved !== undefined,
            },
        }));
    }
    /** The proportion the cut dialog offers first: the format of the step the picture shows in. */
    function cutRatio(target) {
        const step = target.kind === "prize" ? shape.prize : target.kind === "question" ? shape.quiz : shape.slide;
        return step.ratio === "auto" ? "free" : step.ratio;
    }
    /** The cut dialog on a chosen file (D88), or on the original of a stored picture (D44). */
    function cropDialog(on) {
        const common = {
            ratio: cutRatio(on.target),
            busy,
            onCancel: () => {
                setCutting(null);
            },
        };
        if ("file" in on) {
            const chosen = draftOf(on.target);
            return (_jsx(CropDialog, { src: addressOf(on.file), alt: chosen.altText.trim() || on.file.name, initial: chosen.file === on.file ? chosen.crop : null, ...common, onApply: (frame) => {
                    cutChosen(on.target, on.file, frame);
                } }));
        }
        if (writes?.original === undefined) {
            return null;
        }
        return (_jsx(CropDialog, { src: writes.original(on.picture.asset_id), alt: on.picture.alt_text ?? "", initial: on.picture.crop ?? null, ...common, onApply: (frame) => {
                void cut(on.target, on.picture, frame);
            } }));
    }
    /** The draft of one slot, with what it belongs to. */
    function draftOf(target) {
        return drafts[slotKey(target)] ?? EMPTY_SLOT;
    }
    function pick(target, file, altText) {
        const key = slotKey(target);
        if (file === null) {
            // Without a file the card goes: the focus goes to the drop zone, or to »Ersetzen« of a
            // stored file, whose input is hidden.
            const prefix = slotPrefix(position, target);
            focus(pictureAt(form, target) === null ? `${prefix}-datei` : `${prefix}-ersetzen`);
        }
        // The frame belongs to the file it was cut from: typing its description keeps it.
        setDrafts((current) => {
            const before = current[key];
            const crop = before !== undefined && before.file === file ? before.crop : null;
            return { ...current, [key]: { target, file, altText, crop } };
        });
        setMessages((current) => {
            const rest = { ...current };
            delete rest[key];
            return rest;
        });
    }
    /** The address of a chosen file for the preview, made once per file. */
    function addressOf(file) {
        let address = fileAddresses.current.get(file);
        if (address === undefined) {
            address = URL.createObjectURL(file);
            fileAddresses.current.set(file, address);
        }
        return address;
    }
    /**
     * A chosen file cut before the save (D88): records the frame, which goes up with the file,
     * and draws the cut-out for the preview, so the window keeps getting a plain picture.
     */
    function cutChosen(target, file, frame) {
        const key = slotKey(target);
        setDrafts((current) => {
            const before = current[key];
            return before?.file === file ? { ...current, [key]: { ...before, crop: frame } } : current;
        });
        setCutting(null);
        void cutOut(addressOf(file), frame).then((blob) => {
            if (blob !== null) {
                const address = URL.createObjectURL(blob);
                addresses.current.push(address);
                setCuts((current) => ({ ...current, [key]: { file, address } }));
            }
        });
    }
    /** A chosen picture as the browser shows it: its cut-out once drawn, else the whole file. */
    function chosenAddress(target, chosen) {
        const drawn = cuts[slotKey(target)];
        return chosen.crop !== null && drawn?.file === chosen.file ? drawn.address : addressOf(chosen.file);
    }
    /**
     * A stored file as the preview shows it: the browser's copy from this session, else the
     * page's address of the file (D88), so a picture shows after a reload and after a new cut.
     */
    function storedAddress(media) {
        return pictures[media.asset_id] ?? (media.kind === "image" ? writes?.picture?.(media.asset_id) : undefined);
    }
    /** What the preview shows for a slot: the chosen file, the stored picture, a stand-in. */
    function imageOf(target, media) {
        const chosen = draftOf(target);
        if (chosen.file !== null) {
            return isVideoFile(chosen.file)
                ? standIn(chosen.altText, true)
                : { src: chosenAddress(target, { ...chosen, file: chosen.file }), alt: chosen.altText.trim() || chosen.file.name };
        }
        if (media === null) {
            return null;
        }
        const own = storedAddress(media);
        if (own !== undefined) {
            return { src: own, alt: media.alt_text ?? "" };
        }
        return standIn(media.alt_text, isVideo(media));
    }
    /** One slot of the settings, bound to its target. */
    function slot(target, options) {
        const key = slotKey(target);
        const draft = draftOf(target);
        const chosen = draft.file;
        const picture = options.picture;
        // Where the page cuts (`recut`), a chosen picture is cut before its upload (D88) and a
        // stored one from its original (D44).
        let onCrop;
        if (writes?.recut !== undefined && chosen !== null) {
            if (chosen.type.startsWith("image/")) {
                onCrop = () => {
                    setCutting({ target, file: chosen });
                };
            }
        }
        else if (writes?.recut !== undefined && writes.original !== undefined && picture !== null) {
            onCrop = () => {
                setCutting({ target, picture });
            };
        }
        return (_jsx(PictureSlot, { prefix: slotPrefix(position, target), legend: options.legend, place: options.place, picture: picture, shown: picture === null ? undefined : storedAddress(picture), ...(chosen !== null && chosen.type.startsWith("image/")
                ? { preview: chosenAddress(target, { ...draft, file: chosen }) }
                : {}), draft: draft, verdict: !guided, required: options.required === true, message: messages[key] ?? null, unscanned: unscanned.has(key), busy: busy, accepts: options.accepts ?? "picture", onPick: (file, altText) => {
                pick(target, file, altText);
            }, ...(acceptUnscanned && options.unscannedAllowed !== false
                ? {
                    onAcceptUnscanned: (reason) => {
                        void uploadChosen(target, reason);
                    },
                }
                : {}), ...(options.onRemove === undefined ? {} : { onRemove: options.onRemove }), ...(onCrop === undefined ? {} : { onCrop }) }));
    }
    function textChange(apply) {
        return (event) => {
            apply(event.target.value);
        };
    }
    function changeLayout(next) {
        props.onLayoutChange?.(next);
    }
    /** The layout of a step, or nothing where it is locked and left out (`hideLockedLayout`). */
    function layoutSettings(layoutStep, part) {
        return layoutShown ? (_jsx(LayoutSettings, { layout: shape, step: layoutStep, presentation: presentation, idPrefix: id(part), ...(layoutEditable ? { onChange: changeLayout } : {}) })) : null;
    }
    /** One element moved to the place of another in the order of a step's layout. */
    function moveElement(step, element, target) {
        const key = step === "result" ? "quiz" : step;
        changeLayout(withStep(shape, key, { order: droppedElement(shape[key].order, element, target) }));
    }
    /* ------------------------------------------------------------------ the preview */
    /**
     * The text of a slide's own button, typed on the button itself: the big one of every slide but
     * the last (»Weiter«), or the link above it. An emptied »Weiter« gives the slide back the
     * page's text. Both may be the slide's own at once (24.10.2026).
     */
    function slideButtonEditor(target) {
        const slide = form.slides[target.slide ?? -1];
        if (!editable || slide === undefined || (target.labelKey === "link" && !slide.link)) {
            return null;
        }
        const row = `Folie ${String((target.slide ?? 0) + 1)}`;
        if (target.labelKey === "link") {
            const field = id("link-text", slide.key);
            return (_jsx(EditableText, { id: field, ...flagged(field), value: slide.linkLabel, onChange: (linkLabel) => {
                    slides.update(slide.key, () => ({ linkLabel }));
                }, label: `${row}, Beschriftung des Link-Knopfs`, placeholder: FIELD_NAMES.label, maxLength: MAX_LABEL, readOnly: busy }));
        }
        const field = id("knopf-text", slide.key);
        return (_jsx(EditableText, { id: field, ...flagged(field), value: slide.nextLabel, onChange: (nextLabel) => {
                slides.update(slide.key, () => ({ nextLabel }));
            }, label: `${row}, ${FIELD_NAMES.label}`, placeholder: "Weiter", placeholderTone: "page", maxLength: MAX_LABEL, readOnly: busy }));
    }
    /** The in-place editor of a text of the window, or `null` where the text is not edited. */
    function textEditor(target) {
        const readOnly = busy;
        if (target.element === "button") {
            if (target.labelKey === "next" || target.labelKey === "link") {
                return slideButtonEditor(target);
            }
            // The door's own button after a wrong answer (D88); empty, the action's label stands.
            if (target.labelKey === "retry" && quizOpen) {
                return (_jsx(EditableText, { id: id("misserfolg-knopf"), value: form.retryLabel, onChange: (retryLabel) => {
                        setForm((current) => ({ ...current, retryLabel }));
                    }, label: FIELD_NAMES.retry_label, placeholder: shape.labels.retry, placeholderTone: "page", maxLength: MAX_LABEL, readOnly: readOnly }));
            }
            if (!layoutEditable || target.labelKey === undefined) {
                return null;
            }
            const key = target.labelKey;
            const names = { to_quiz: "Knopf zum Quiz", submit: "Knopf zum Abschicken", retry: "Knopf nach falscher Antwort" };
            return (_jsx(EditableText, { id: id(`vorschau-${key}`), value: shape.labels[key], onChange: (value) => {
                    changeLayout({ ...shape, labels: { ...shape.labels, [key]: value } });
                }, label: names[key], maxLength: MAX_LABEL, readOnly: readOnly }));
        }
        if (!editable) {
            return null;
        }
        if (target.step === "slide") {
            const slide = form.slides[target.slide ?? -1];
            if (slide === undefined) {
                return null;
            }
            const row = slideName(target.slide ?? 0);
            return target.element === "title" ? (_jsx(EditableText, { id: id("titel", slide.key), value: slide.title, onChange: (title) => {
                    slides.update(slide.key, () => ({ title }));
                }, label: `${row}, ${FIELD_NAMES.title}`, placeholder: FIELD_NAMES.title, maxLength: 120, readOnly: readOnly })) : target.element === "text" ? (_jsx(EditableText, { id: id("text", slide.key), value: slide.body, onChange: (body) => {
                    slides.update(slide.key, () => ({ body }));
                }, label: `${row}, ${FIELD_NAMES.body}`, placeholder: FIELD_NAMES.body, multiline: true, formatting: { links: linkButtons }, 
                // FallbackRequest on the server; a slide of a door takes 600 (BODY_MAX_LENGTH).
                maxLength: fallback ? 20_000 : 600, readOnly: readOnly })) : null;
        }
        if (target.step === "quiz" && quizOpen) {
            // A single question is the window's title, as on the participant page: its one editor.
            const first = form.questions[0];
            if (target.element === "title" && first !== undefined) {
                return (_jsx(EditableText, { id: id("frage", first.key), ...flagged(id("frage", first.key)), value: first.text, onChange: (text) => {
                        questions.update(first.key, () => ({ text }));
                    }, label: `Frage 1, ${FIELD_NAMES.question_text}`, placeholder: FIELD_NAMES.question_text, maxLength: 500, readOnly: readOnly }));
            }
            return target.element === "text" ? (_jsx(EditableText, { id: id("begruessung"), value: form.intro, onChange: (intro) => {
                    setForm((current) => ({ ...current, intro }));
                }, label: FIELD_NAMES.intro, placeholder: FIELD_NAMES.intro, multiline: true, formatting: { links: linkButtons }, maxLength: 600, readOnly: readOnly })) : null;
        }
        if (target.step === "prize" && target.element === "text" && prizeOpen) {
            // Named above it (D88): a prize reads like any sentence, and the editor's name is unseen.
            return (_jsxs("div", { children: [_jsx("span", { "aria-hidden": "true", className: "block font-sans text-xs font-medium text-ink-muted", children: FIELD_NAMES.prize_description }), _jsx(EditableText, { id: id("gewinn"), value: form.prizeDescription, onChange: (prizeDescription) => {
                            setForm((current) => ({ ...current, prizeDescription }));
                        }, label: FIELD_NAMES.prize_description, placeholder: FIELD_NAMES.prize_description, maxLength: 2000, readOnly: readOnly })] }));
        }
        // The heading of a wrong result is the page's own and stays fixed (02.11.2026).
        if (target.step === "result" && target.element === "text" && quizOpen) {
            return (_jsxs(_Fragment, { children: [_jsx(EditableText, { id: id("misserfolg"), value: form.failureText, onChange: (failureText) => {
                            setForm((current) => ({ ...current, failureText }));
                        }, label: FIELD_NAMES.failure_text, placeholder: FIELD_NAMES.failure_text, multiline: true, formatting: { links: linkButtons }, maxLength: 300, readOnly: readOnly }), _jsx("p", { children: _jsx(EditableText, { id: id("misserfolg-hinweis"), value: form.failureNote, onChange: (failureNote) => {
                                setForm((current) => ({ ...current, failureNote }));
                            }, label: FIELD_NAMES.failure_note, placeholder: wrongAnswerWords(form.questions.length).sentence, placeholderTone: "page", maxLength: 300, readOnly: readOnly }) })] }));
        }
        return null;
    }
    /** The rail step an element of the window belongs to. */
    function railStepOf(target) {
        switch (target.step) {
            case "slide": {
                const slide = form.slides[target.slide ?? 0];
                return slide === undefined ? { kind: "quiz" } : { kind: "slide", key: slide.key };
            }
            case "quiz":
                return { kind: "quiz" };
            case "prize":
                return { kind: "right" };
            default:
                return { kind: "wrong" };
        }
    }
    const isChosen = (target) => chosenElement === target.element && sameStep(railStepOf(target), step);
    /**
     * An element of the window: chosen by a click, by the focus or by Enter, and with the layout
     * open a drop target. The chosen one carries its settings right under it (D88). The keyboard
     * reaches an element through its text editor where it has one, else the element itself.
     */
    function item(target) {
        const element = target.element;
        const own = {
            onSelect: () => {
                choose(railStepOf(target), element);
            },
            focusable: textEditor(target) === null,
            controls: controlsOf(target),
        };
        if (!layoutEditable || element === "button") {
            return { className: isChosen(target) ? CHOSEN_CLASS : CHOOSABLE_CLASS, ...own };
        }
        return {
            className: `${isChosen(target) || over === element ? CHOSEN_CLASS : CHOOSABLE_CLASS} ${MOVABLE_CLASS}`,
            ...own,
            onDragOver: (event) => {
                if (dragged.current !== null) {
                    event.preventDefault();
                    setOver(element);
                }
            },
            onDragLeave: () => {
                setOver(null);
            },
            onDrop: (event) => {
                event.preventDefault();
                setOver(null);
                const source = dragged.current;
                dragged.current = null;
                if (source !== null && source !== element) {
                    moveElement(target.step, source, element);
                }
            },
        };
    }
    /**
     * The grip of a picture, a title or a text, after `Movable` of the form page: the mouse drags
     * it onto another element, the arrow keys move it one place. On the chosen element two buttons
     * beside it do the same, each only where it moves something (D53): a phone neither drags
     * reliably nor has arrow keys. It shows under the mouse, with the focus and on the chosen
     * element.
     */
    function grip(target, shown) {
        const element = target.element;
        if (element === "button") {
            return null;
        }
        const gripId = id(`griff-${element}`);
        const label = ELEMENT_LABELS[element];
        const index = shown.indexOf(element);
        const chosen = isChosen(target);
        /** One place up or down, past the next element that shows; the focus stays where it moves on. */
        const move = (by, stays) => {
            const neighbour = shown[index + by];
            if (neighbour !== undefined) {
                focus(stays);
                moveElement(target.step, element, neighbour);
            }
        };
        const step = (by) => {
            const up = by < 0;
            // At the edge after this move its own button is gone, so the other one takes the focus.
            const atEdge = up ? index + by === 0 : index + by === shown.length - 1;
            return !chosen || shown[index + by] === undefined ? null : (_jsx("button", { id: id(`griff-${element}-${up ? "hoch" : "runter"}`), type: "button", disabled: busy, className: GRIP_CLASS, "aria-label": `${label} ${up ? "nach oben" : "nach unten"}`, onClick: () => {
                    move(by, id(`griff-${element}-${atEdge === up ? "runter" : "hoch"}`));
                }, children: _jsx(Icon, { name: up ? "up" : "down" }) }));
        };
        return (
        // door.css spaces the children of a text; the grips stand outside that flow.
        _jsxs("span", { className: chosen ? GRIPS_CLASS : `${GRIPS_CLASS} ${GRIP_HIDDEN_CLASS}`, style: { margin: 0 }, children: [step(-1), step(1), _jsx("button", { id: gripId, type: "button", draggable: !busy, disabled: busy, className: `${GRIP_CLASS} cursor-grab`, "aria-label": `${label} verschieben`, title: "Ziehen oder mit den Pfeiltasten verschieben", onKeyDown: (event) => {
                        if (event.key !== "ArrowUp" && event.key !== "ArrowDown") {
                            return;
                        }
                        event.preventDefault();
                        move(event.key === "ArrowUp" ? -1 : 1, gripId);
                    }, onDragStart: (event) => {
                        // Firefox starts no drag without data.
                        event.dataTransfer?.setData("text/plain", element);
                        if (event.dataTransfer) {
                            event.dataTransfer.effectAllowed = "move";
                        }
                        dragged.current = element;
                    }, onDragEnd: () => {
                        dragged.current = null;
                        setOver(null);
                    }, children: _jsx(Icon, { name: "grip" }) })] }));
    }
    const text = (target, shown) => textEditor(target) ?? shown;
    // Elements are chosen only where something can be changed; else the preview is a view.
    const edit = !editable && !layoutEditable
        ? { text }
        : { text, item, addLink, ...(layoutEditable ? { move: grip } : {}) };
    // In the editor an empty element still shows where it can be filled or moved (22.10.2026):
    // the place of a picture, the name of a title or a text. Else it could not be chosen.
    const fills = (open) => open || layoutEditable;
    /** A text of the draft as the window shows it; empty, a stand-in the editor replaces. */
    const blocksOf = (text, open = editable, name = "") => {
        const blocks = plainBlocks(text);
        if (blocks.length > 0) {
            return blocks;
        }
        return open ? EDITABLE_BLOCK : layoutEditable ? plainBlocks(name) : [];
    };
    const titleOf = (text, name, open = editable) => text.trim() !== "" ? text : fills(open) ? name : null;
    const pictureOf = (image, open) => image ?? (fills(open) ? EMPTY_PICTURE : null);
    function windowProps() {
        const base = {
            layout: { ...shape, retry_slide: retrySlideIn(shape, form.slides.length) },
            presentation,
            sponsored,
            edit,
        };
        if (step.kind === "slide") {
            const active = Math.max(0, form.slides.findIndex((slide) => slide.key === step.key));
            return {
                ...base,
                step: "slide",
                activeSlide: active,
                quizHref: fallback ? null : "#quiz",
                slides: form.slides.map((slide) => ({
                    title: fallback ? null : titleOf(slide.title, FIELD_NAMES.title),
                    blocks: blocksOf(slide.body, editable, FIELD_NAMES.body),
                    image: pictureOf(imageOf({ kind: "slide", key: slide.key }, slide.media), editable),
                    button: slide.nextLabel.trim() === "" || fallback ? null : { label: slide.nextLabel.trim(), action: "next" },
                    link: !slide.link || fallback
                        ? null
                        : { label: slide.linkLabel.trim(), action: "link", url: slide.linkUrl.trim() || null },
                })),
            };
        }
        if (step.kind === "quiz") {
            const shown = form.questions.filter((question) => quizOpen || !isEmptyQuestion(question));
            const [first] = shown;
            return {
                ...base,
                step: "quiz",
                panel: {
                    image: pictureOf(first === undefined ? null : imageOf({ kind: "question", key: first.key }, first.image), quizOpen),
                    title: shown.length === 1 && first !== undefined
                        ? titleOf(first.text, FIELD_NAMES.question_text, quizOpen)
                        : null,
                    blocks: blocksOf(form.intro, quizOpen, FIELD_NAMES.intro),
                },
                notice: quizOpen ? questionList() : _jsx(QuestionsStill, { questions: shown }),
            };
        }
        if (step.kind === "right") {
            return {
                ...base,
                step: "prize",
                panel: {
                    image: pictureOf(imageOf({ kind: "prize" }, form.prizeImage), prizeOpen),
                    paragraphs: form.prizeDescription.trim() !== ""
                        ? [form.prizeDescription]
                        : prizeOpen
                            ? [" "]
                            : layoutEditable
                                ? [FIELD_NAMES.prize_description]
                                : [],
                },
                notice: quizOpen ? (_jsx(EditableText, { id: id("erfolg"), value: form.successText, onChange: (successText) => {
                        setForm((current) => ({ ...current, successText }));
                    }, label: FIELD_NAMES.success_text, placeholder: FIELD_NAMES.success_text, multiline: true, formatting: { links: linkButtons }, maxLength: 300, readOnly: busy })) : form.successText.trim() !== "" ? (_jsx("p", { children: form.successText })) : null,
                form: (_jsx("p", { className: "border border-dashed border-line p-3 text-sm text-ink-muted", children: "Teilnahmeformular" })),
            };
        }
        const words = wrongAnswerWords(form.questions.length);
        return {
            ...base,
            step: "result",
            panel: {
                title: words.title,
                blocks: blocksOf(form.failureText, quizOpen),
                // With the text editor the sentence stands under it (`textEditor`).
                paragraphs: quizOpen
                    ? []
                    : [withCounts(form.failureNote.trim(), form.questions.length) || words.sentence],
            },
            action: { label: form.retryLabel.trim() || shape.labels.retry, href: "#", labelKey: "retry" },
        };
    }
    // The number of prizes stands above the window of its step (D88), not under an element: the
    // problem list and »Noch offen« lead to it without choosing one.
    const quantity = step.kind === "right" && prizeOpen && prizeQuantity ? (_jsxs("fieldset", { disabled: busy, className: "m-0 mb-3 min-w-0 border-0 p-0 font-sans text-ink sm:max-w-xs", children: [_jsx("legend", { className: "sr-only", children: "Gewinn" }), _jsx("label", { className: "block text-sm font-medium", htmlFor: id("anzahl"), children: FIELD_NAMES.prize_quantity }), _jsx("input", { id: id("anzahl"), className: FIELD_CLASS, type: "number", min: 1, max: 1000, step: 1, ...flagged(id("anzahl")), value: form.prizeQuantity, onChange: textChange((typed) => {
                    setForm((current) => ({ ...current, prizeQuantity: typed }));
                }) }), _jsxs("div", { className: "mt-2 flex items-start gap-2", children: [_jsx("input", { id: id("digital"), className: "mt-1", type: "checkbox", checked: form.prizeDigital, onChange: (event) => {
                            const prizeDigital = event.target.checked;
                            setForm((current) => ({ ...current, prizeDigital }));
                        } }), _jsx("label", { className: "text-sm", htmlFor: id("digital"), children: FIELD_NAMES.prize_digital })] })] })) : null;
    const preview = (_jsxs("div", { ref: inPreview, children: [quantity, _jsx(DoorWindow, { ...windowProps() })] }));
    /* ---------------------------------------------------------------- the settings */
    /**
     * The settings of the chosen element (D49, D88), in a box right under it in the window: a
     * picture's file, place and format and a video's parts, a button's kind and address, the way
     * back after a wrong answer. Locked while a write runs. A title, a text and a button's text
     * are typed in place and have none, the quiz's questions too (`questionList`).
     */
    function controlsOf(target) {
        const content = isChosen(target) ? settingsOf(target) : null;
        return content === null ? null : (_jsxs("fieldset", { disabled: busy, className: CONTROLS_CLASS, children: [_jsx("legend", { className: "sr-only", children: target.element === "button" ? "Knopf" : ELEMENT_LABELS[target.element] }), content] }));
    }
    /** What `controlsOf` shows of an element, `null` where nothing would show. */
    function settingsOf(target) {
        const index = target.slide ?? 0;
        const slide = target.step === "slide" ? form.slides[index] : undefined;
        // The counts a wrong result's own words may name; the page puts in the numbers.
        if (target.step === "result" && target.element === "text" && quizOpen) {
            return (_jsxs(Help, { name: "Hilfe zu den Zahlen", children: ["{richtig}", " setzt die Zahl der richtigen Antworten ein, ", "{fragen}", " die Zahl der Fragen, z. B. \u00BB", "{richtig}", " von ", "{fragen}", " Fragen sind richtig.\u00AB"] }));
        }
        if (target.element === "image") {
            if (slide !== undefined) {
                return slideImageSettings(slide, index);
            }
            if (target.step === "quiz") {
                // The first question's picture is the window's, as on the participant page.
                const first = form.questions[0];
                return both(quizOpen && first !== undefined ? questionSlot(first, "Frage 1") : null, layoutSettings("quiz", "aufbau-quiz"));
            }
            if (target.step !== "prize") {
                return null;
            }
            const picture = prizeOpen
                ? slot({ kind: "prize" }, { legend: "Gewinnbild", place: "Gewinn", picture: form.prizeImage })
                : null;
            return both(picture, layoutSettings("prize", "aufbau-gewinn"));
        }
        if (target.element !== "button") {
            return null;
        }
        // A slide's own button, after its big one: the link above it is the same button's.
        if (slide !== undefined) {
            return target.labelKey === "link" ? null : slideButtonSettings(slide, index);
        }
        return target.step === "result" ? retrySettings() : null;
    }
    /** The picture's file, a video's parts and the layout of the picture, as far as they show. */
    function slideImageSettings(slide, index) {
        const title = slideName(index);
        const target = { kind: "slide", key: slide.key };
        return both(editable ? (_jsxs(_Fragment, { children: [slot(target, {
                    legend: fallback ? "Bild zum Ersatzinhalt" : `${videos ? "Bild oder Video" : "Bild"} zu ${title}`,
                    place: title,
                    picture: slide.media,
                    accepts: videos ? "slide" : "picture",
                    onRemove: () => {
                        focus(`${id("bild", slide.key)}-datei`);
                        slides.update(slide.key, () => ({ media: null }));
                    },
                }), videos && slide.media !== null && isVideo(slide.media) ? videoSettings(slide, title) : null] })) : null, layoutSettings("slide", "aufbau-folie"));
    }
    /**
     * »+ Knopf« above a slide's big button (24.10.2026): adds the link out. The big button always
     * leads on, to the next slide or after the last one to the quiz; only its text is the slide's.
     */
    function addLink(index) {
        const slide = form.slides[index];
        if (!editable || !linkButtons || slide === undefined) {
            return null;
        }
        return (_jsxs("button", { id: id("knopf-neu", slide.key), type: "button", className: ADD_CLASS, style: { alignSelf: "flex-start" }, disabled: busy, "aria-label": `${slideName(index)}: Link-Knopf hinzufügen`, onClick: () => {
                slides.update(slide.key, () => ({ link: true }));
                choose({ kind: "slide", key: slide.key }, "button");
                focus(id("link-text", slide.key));
            }, children: [_jsx(Icon, { name: "plus" }), " Knopf"] }));
    }
    /** The address of a slide's link and the way to remove it; the text is typed on the link. */
    function slideButtonSettings(slide, index) {
        if (!editable || !slide.link) {
            return null;
        }
        const title = slideName(index);
        const remove = linkButtons ? (_jsxs("button", { id: id("knopf-weg", slide.key), type: "button", className: `${ADD_CLASS} mt-3`, disabled: busy, onClick: () => {
                slides.update(slide.key, () => ({ link: false, linkLabel: "", linkUrl: "" }));
                focus(id("knopf-neu", slide.key));
            }, children: [_jsx(Icon, { name: "close" }), " Link-Knopf entfernen"] })) : null;
        return both(linkButtons ? (_jsxs("div", { children: [_jsxs("label", { className: LABEL_CLASS, htmlFor: id("knopf-adresse", slide.key), children: [_jsx(InRow, { row: title }), FIELD_NAMES.url] }), _jsx("input", { id: id("knopf-adresse", slide.key), className: FIELD_CLASS, type: "url", inputMode: "url", maxLength: 500, ...flagged(id("knopf-adresse", slide.key)), value: slide.linkUrl, onChange: textChange((linkUrl) => {
                        slides.update(slide.key, () => ({ linkUrl }));
                    }) })] })) : slide.linkUrl.trim() === "" ? null : (_jsxs("p", { className: "mt-3 text-sm", children: [_jsx(InRow, { row: title }), FIELD_NAMES.url, ": ", slide.linkUrl] })), remove);
    }
    /** What a video needs before the submission (package H). */
    function videoSettings(slide, title) {
        if (slide.media === null) {
            return null;
        }
        return (_jsxs("fieldset", { className: "mt-6 min-w-0", children: [_jsxs("legend", { className: "font-sans text-sm font-semibold", children: ["Video", _jsxs("span", { className: "sr-only", children: [" von ", title] })] }), slot({ kind: "poster", key: slide.key }, {
                    legend: "Vorschaubild",
                    place: `${title}, Vorschaubild`,
                    picture: slide.media.poster ?? null,
                    unscannedAllowed: false,
                    required: true,
                }), _jsxs("div", { className: "mt-4 flex flex-wrap items-center gap-2", children: [_jsxs("label", { className: "text-sm font-medium", htmlFor: id("textfassung", slide.key), children: [_jsx(InRow, { row: title }), "Textfassung des Videos", _jsx(Required, {})] }), _jsx(Help, { name: "Hilfe zur Textfassung", children: "Was im Video gesagt und gezeigt wird, f\u00FCr Menschen, die es nicht sehen oder h\u00F6ren. Teilnehmende klappen die Textfassung unter dem Video auf." })] }), _jsx("textarea", { id: id("textfassung", slide.key), className: FIELD_CLASS, "aria-required": "true", rows: 5, maxLength: 5000, value: slide.textAlternative, onChange: textChange((textAlternative) => {
                        slides.update(slide.key, () => ({ textAlternative }));
                    }) }), slot({ kind: "captions", key: slide.key }, {
                    legend: "Untertitel",
                    place: `${title}, Untertitel`,
                    picture: slide.media.captions ?? null,
                    accepts: "captions",
                    unscannedAllowed: false,
                }), _jsxs("div", { className: "mt-4 flex items-start gap-2", children: [_jsx("input", { id: id("ohne-untertitel", slide.key), className: "mt-1", type: "checkbox", checked: slide.needsNoCaptions, onChange: (event) => {
                                const needsNoCaptions = event.target.checked;
                                slides.update(slide.key, () => ({ needsNoCaptions }));
                            } }), _jsxs("label", { className: "text-sm", htmlFor: id("ohne-untertitel", slide.key), children: [_jsx(InRow, { row: title }), "Das Video enth\u00E4lt keine gesprochene Sprache, oder die Untertitel sind im Bild."] })] })] }));
    }
    /**
     * A question's picture: it hangs on a stored question, so the slot comes with the question's
     * text and stays while it holds a picture or a chosen file.
     */
    function questionSlot(question, title) {
        const target = { kind: "question", key: question.key };
        if (isEmptyQuestion(question) && question.image === null && draftOf(target).file === null) {
            return null;
        }
        return slot(target, {
            legend: `Bild zu ${title}`,
            place: title,
            picture: question.image,
            onRemove: () => {
                focus(`${id("bild", question.key)}-datei`);
                questions.update(question.key, () => ({ image: null }));
            },
        });
    }
    /**
     * One question in the window (D88), where the participant reads it: its text, from the second
     * question on its picture, as the answer form shows them, and its answers, each with its mark
     * as the right one. A single question's text is the window's title and typed there.
     */
    function questionEditor(question, index) {
        const title = `Frage ${String(index + 1)}`;
        const keys = OPTION_KEYS.slice(0, question.optionCount);
        return (_jsxs("li", { className: ROW_CLASS, children: [_jsx(RowHead, { title: title, index: index, count: form.questions.length, id: (part) => id(part, question.key), onMove: (by) => {
                        questions.move(index, by);
                    }, onRemove: () => {
                        questions.remove(index);
                    } }), question.archived ? (_jsx("p", { className: "mt-1 font-sans text-sm", children: "Archiviert. Bitte pr\u00FCfen und speichern, dann ist die Frage wieder in Gebrauch." })) : null, form.questions.length > 1 ? (_jsx("p", { className: "mt-1 font-semibold", children: _jsx(EditableText, { id: id("frage", question.key), ...flagged(id("frage", question.key)), value: question.text, onChange: (text) => {
                            questions.update(question.key, () => ({ text }));
                        }, label: `${title}, ${FIELD_NAMES.question_text}`, placeholder: FIELD_NAMES.question_text, maxLength: 500, readOnly: busy }) })) : null, index === 0 ? null : questionSlot(question, title), _jsxs("div", { role: "group", "aria-label": `${title}, ${FIELD_NAMES.answer_options}`, className: "mt-2 min-w-0", children: [keys.map((key, at) => {
                            const right = id(`richtig-${key}`, question.key);
                            const letter = key.toUpperCase();
                            const last = at === keys.length - 1;
                            return (_jsxs("div", { className: "mt-1 flex items-center gap-2", children: [_jsx("span", { "aria-hidden": "true", children: "\u25CB" }), _jsx("span", { className: "min-w-0 flex-1", children: _jsx(EditableText, { id: id(`antwort-${key}`, question.key), ...flagged(id(`antwort-${key}`, question.key)), value: question.options[key], onChange: (value) => {
                                                // A cleared answer cannot stay the right one: the server would drop the
                                                // mark on save anyway, and a checked, disabled button says nothing.
                                                questions.update(question.key, (row) => ({
                                                    options: { ...row.options, [key]: value },
                                                    correct: value.trim() === "" && row.correct === key ? "" : row.correct,
                                                }));
                                            }, label: `${title}, Antwort ${letter}`, placeholder: `Antwort ${letter}`, maxLength: 200, readOnly: busy }) }), _jsx("span", { className: "w-20 shrink-0 font-sans", children: question.options[key].trim() === "" ? null : (_jsxs("label", { className: "inline-flex items-center gap-1.5 text-sm", htmlFor: right, children: [_jsx("input", { id: right, ...flagged(right), type: "radio", name: id("richtig", question.key), value: key, checked: question.correct === key, onChange: () => {
                                                        questions.update(question.key, () => ({ correct: key }));
                                                    } }), "richtig", _jsxs("span", { className: "sr-only", children: [": Antwort ", letter] })] })) }), last && question.optionCount > MIN_OPTIONS ? (_jsx("button", { id: id("antwort-weg", question.key), type: "button", className: ICON_CLASS, "aria-label": `${title}: letzte Antwort entfernen`, title: "Antwort entfernen", onClick: () => {
                                            if (question.optionCount - 1 <= MIN_OPTIONS) {
                                                focus(id("antwort-neu", question.key));
                                            }
                                            questions.update(question.key, (row) => ({
                                                optionCount: Math.max(MIN_OPTIONS, row.optionCount - 1),
                                                options: { ...row.options, [key]: "" },
                                                correct: row.correct === key ? "" : row.correct,
                                            }));
                                        }, children: _jsx(Icon, { name: "close" }) })) : (_jsx("span", { className: "size-8 shrink-0" }))] }, key));
                        }), question.optionCount >= MAX_OPTIONS ? null : (_jsx("button", { id: id("antwort-neu", question.key), type: "button", className: `${QUIET_CLASS} mt-1`, "aria-label": `${title}: Antwort hinzufügen`, onClick: () => {
                                const next = OPTION_KEYS[question.optionCount];
                                if (next !== undefined) {
                                    focus(id(`antwort-${next}`, question.key));
                                }
                                questions.update(question.key, (row) => ({
                                    optionCount: Math.min(MAX_OPTIONS, row.optionCount + 1),
                                }));
                            }, children: "+ Antwort" }))] })] }, question.key));
    }
    /**
     * The buttons on the tab of the shown slide (23.10.2026), inside the tab behind a thin line
     * (24.10.2026): ‹ › move it in the order, each only where it moves something; the small red x
     * removes it. The focus lands on a button that is still there (`rowsOf`).
     */
    function slideTools(index) {
        const slide = form.slides[index];
        if (!editable || fallback || !slideStructure || slide === undefined) {
            return null;
        }
        const title = slideName(index);
        const move = (by) => {
            const up = by < 0;
            if (up ? index === 0 : index === form.slides.length - 1) {
                return null;
            }
            return (_jsx("button", { id: id(up ? "hoch" : "runter", slide.key), type: "button", className: TAB_TOOL_CLASS, disabled: busy, "aria-label": `${title} ${up ? "nach links" : "nach rechts"}`, title: up ? "Nach links" : "Nach rechts", onClick: () => {
                    slides.move(index, by);
                }, children: _jsx(Icon, { name: up ? "left" : "right" }) }));
        };
        return (_jsxs("span", { className: TAB_TOOLS_CLASS, children: [move(-1), move(1), _jsx("button", { id: id("entfernen", slide.key), type: "button", className: TAB_REMOVE_CLASS, disabled: busy, "aria-label": `${title} entfernen`, title: "Entfernen", onClick: () => {
                        const neighbour = form.slides[index + 1] ?? form.slides[index - 1];
                        const next = neighbour === undefined ? { kind: "quiz" } : { kind: "slide", key: neighbour.key };
                        choose(next);
                        slides.remove(index);
                        // Still at the limit, »Folie hinzufügen« stays away: the step takes the focus.
                        if (form.slides.length - 1 >= slideLimit) {
                            focus(railId(next));
                        }
                    }, children: _jsx(Icon, { name: "close" }) })] }));
    }
    /** The way back after a wrong answer, the setting of its button; the text is typed on it. */
    function retrySettings() {
        if (!layoutShown) {
            return null;
        }
        // Offer the slides the preview shows. A stored value beyond a door's last slide is clamped
        // on the way out (retrySlideIn, DoorLayout.fitted), so a door with fewer goes to its last.
        const slideCount = Math.max(1, form.slides.length);
        return (_jsxs("div", { className: "sm:max-w-xs", children: [_jsx("label", { className: LABEL_CLASS, htmlFor: id("ruecksprung"), children: "Zur\u00FCck auf" }), _jsx("select", { id: id("ruecksprung"), className: FIELD_CLASS, value: retrySlideIn(shape, slideCount), disabled: !layoutEditable, onChange: (event) => {
                        changeLayout({ ...shape, retry_slide: Number(event.target.value) });
                    }, children: Array.from({ length: slideCount }, (_, index) => (_jsxs("option", { value: index + 1, children: ["Folie ", index + 1] }, index))) })] }));
    }
    /** The questions of the quiz in the window, where they may be changed; locked during a write. */
    function questionList() {
        return (_jsxs("fieldset", { disabled: busy, className: "m-0 min-w-0 border-0 p-0", children: [_jsx("legend", { className: "sr-only", children: "Fragen" }), _jsx("ol", { className: "m-0 list-none space-y-6 p-0", children: form.questions.map((question, index) => questionEditor(question, index)) }), form.questions.length >= MAX_QUESTIONS ? null : (_jsxs("button", { id: id("frage-neu"), type: "button", className: `${ADD_CLASS} mt-4`, "aria-label": "Frage hinzuf\u00FCgen", onClick: () => {
                        questions.add(emptyQuestion());
                    }, children: [_jsx(Icon, { name: "plus" }), " Frage"] }))] }));
    }
    // The action's standard layout (`layoutOnly`) is one for every slide: one tab »Folien«.
    const railSlides = props.layoutOnly === true ? form.slides.slice(0, 1) : form.slides;
    const rail = [
        ...railSlides.map((slide, index) => ({
            step: { kind: "slide", key: slide.key },
            label: props.layoutOnly === true ? "Folien" : slideName(index),
        })),
        ...(fallback
            ? []
            : [
                { step: { kind: "quiz" }, label: "Quiz" },
                // The whole window after a right answer: picture, prize, success text, form.
                { step: { kind: "right" }, label: "Gewinn" },
                { step: { kind: "wrong" }, label: guided ? "Falsche Antwort" : "Falsch" },
            ]),
    ];
    const at = rail.findIndex((entry) => sameStep(entry.step, step));
    const following = rail[at + 1];
    // »+« after the last slide, where the new one goes; at the limit, the door's or the
    // sponsor's, it goes.
    const addSlide = canAddSlide ? (_jsx("li", { children: _jsx("button", { id: id("folie-neu"), type: "button", className: "inline-flex size-8 items-center justify-center rounded-md text-ink-muted hover:bg-surface hover:text-ink disabled:cursor-not-allowed disabled:opacity-50", disabled: busy, "aria-label": "Folie hinzuf\u00FCgen", title: "Folie hinzuf\u00FCgen", onClick: () => {
                const row = emptySlide();
                choose({ kind: "slide", key: row.key }, "title");
                slides.add(row);
            }, children: _jsx(Icon, { name: "plus" }) }) })) : null;
    return (_jsxs("div", { ref: root, className: "mt-4 min-w-0", children: [props.notice === undefined ? null : _jsx("div", { className: NOTE_CLASS, children: props.notice }), _jsxs("div", { className: "mt-4 flex flex-wrap items-start gap-2", children: [_jsx("nav", { "aria-label": "Schritte", className: "mr-auto", children: _jsxs("ol", { className: "inline-flex flex-wrap items-center gap-1 rounded-md bg-surface-sunken p-1", children: [form.slides.length === 0 ? addSlide : null, rail.map((entry, index) => {
                                    const current = sameStep(entry.step, step);
                                    const tools = current && entry.step.kind === "slide" ? slideTools(index) : null;
                                    return (_jsxs(Fragment, { children: [_jsxs("li", { className: tools === null ? "inline-flex items-center" : TAB_GROUP_CLASS, children: [_jsx("button", { id: railId(entry.step), type: "button", className: tools === null ? (current ? TAB_CURRENT_CLASS : TAB_CLASS) : TAB_IN_GROUP_CLASS, "aria-current": current ? "step" : undefined, onClick: () => {
                                                            choose(entry.step);
                                                        }, children: entry.label }), tools] }), index === form.slides.length - 1 ? addSlide : null] }, entry.step.kind === "slide" ? entry.step.key : entry.step.kind));
                                })] }) }), editable ? _jsx(Help, { name: "Hilfe zur Formatierung", children: formatHint(linkButtons) }) : null] }), props.layoutNote === undefined ? null : _jsx("div", { className: NOTE_CLASS, children: props.layoutNote }), guided && !viewOnly && missing.length > 0 ? (_jsxs("div", { className: "mt-4 flex flex-wrap items-baseline gap-x-3 gap-y-1 rounded-md bg-notice-soft px-3 py-2 text-sm", children: [_jsx("p", { className: "font-medium", id: id("offen"), children: "Noch offen:" }), _jsx("ul", { className: "flex flex-wrap gap-x-3 gap-y-1", "aria-labelledby": id("offen"), children: missing.map((part) => {
                            const target = offered(part.key) ? missingTarget(form, part.key, position) : null;
                            return (_jsx("li", { children: target === null ? (part.label) : (
                                // A jump to the input; the router of the page must not see the address.
                                _jsx("a", { className: "underline underline-offset-4", href: `#${target.field}`, onClick: (event) => {
                                        event.preventDefault();
                                        jump(target.step, target.field, target.element);
                                    }, children: part.label })) }, part.key));
                        }) })] })) : null, _jsx("div", { className: `mt-4 min-w-0 ${presentation === "dialog" ? "max-w-[40rem]" : "max-w-[46rem]"}`, children: props.frame === undefined ? preview : props.frame(preview) }), problems.length > 0 ? (_jsxs("div", { id: id("probleme"), tabIndex: -1, className: "mt-6 border border-notice bg-notice-soft px-4 py-3 text-sm text-notice", role: "alert", children: [_jsx("p", { className: "font-medium", children: "Bitte zuerst korrigieren:" }), _jsx("ul", { className: "mt-1 list-disc pl-5", children: problems.map((problem) => (_jsx("li", { id: `${problem.field}-fehler`, children: _jsx("a", { className: "underline underline-offset-4", href: `#${problem.field}`, onClick: (event) => {
                                    event.preventDefault();
                                    jump(problem.step, problem.field, problem.element);
                                }, children: problem.message }) }, problem.field))) })] })) : null, guided && !viewOnly && following !== undefined ? (_jsx("button", { id: id("weiter"), type: "button", className: `${BUTTON_CLASS} mt-4`, "aria-label": `Weiter: ${following.label}`, onClick: () => {
                    choose(following.step);
                    // From the last but one step »Weiter« goes: the step's own button takes the focus.
                    focus(rail[at + 2] === undefined ? railId(following.step) : id("weiter"));
                }, children: "Weiter" })) : null, writes !== undefined ? (_jsxs(_Fragment, { children: [submitNoted === undefined ? null : (_jsx("p", { id: submitNoted, className: NOTE_CLASS, children: props.submitNote })), _jsxs("div", { className: "mt-6 flex flex-wrap items-center gap-2", children: [inBar ? null : (_jsx("button", { id: id("speichern"), type: "button", className: writes.submit === undefined ? PRIMARY_CLASS : BUTTON_CLASS, disabled: busy, "aria-describedby": warned, onClick: () => {
                                    void save(false);
                                }, children: "Speichern" })), writes.submit === undefined ? null : (_jsx("button", { id: id("einreichen"), type: "button", className: PRIMARY_CLASS, disabled: busy, "aria-describedby": submitDescribed, onClick: () => {
                                    void save(true);
                                }, children: props.submitLabel ?? "Speichern und einreichen" })), dirty ? _jsx("span", { className: "text-sm text-ink-muted", children: "Ungespeicherte \u00C4nderungen" }) : null] }), _jsx("div", { role: "status", children: warned === undefined ? null : (_jsx("p", { id: warned, className: NOTE_CLASS, children: "Dieser Inhalt wurde inzwischen an anderer Stelle gespeichert. \u00BBSpeichern\u00AB ersetzt diesen Stand durch die Eingaben hier." })) }), said === null || (said.ok && changed) ? null : (_jsx("p", { className: "mt-2 text-sm", children: said.message ?? "Erledigt." }))] })) : null, cutting === null
                ? null
                : createPortal(cropDialog(cutting), root.current?.closest('[role="dialog"]') ?? document.body)] }));
}
/**
 * The questions of the quiz as the answer form shows them, standing still, where they may not
 * be changed: the form itself is the participant page's island. One question is the window's
 * title; several keep their own.
 */
function QuestionsStill({ questions, }) {
    const several = questions.length > 1;
    return (_jsx("div", { className: "grid gap-3", children: questions.map((question, index) => (_jsxs("div", { children: [several ? (_jsxs("p", { className: "font-semibold", children: ["Frage ", index + 1, ": ", question.text] })) : null, OPTION_KEYS.slice(0, question.optionCount)
                    .filter((key) => question.options[key].trim() !== "")
                    .map((key) => (_jsxs("p", { children: [_jsx("span", { "aria-hidden": "true", children: "\u25CB " }), question.options[key]] }, key)))] }, question.key))) }));
}
//# sourceMappingURL=DoorConfigurator.js.map